/**
 * The alert rule form (New / Edit alert rule, both apps) — two columns:
 *
 *   left, the questions                          right, always in view (sticky)
 *   1  What to watch for   name · main event      how the alert will read on the board
 *                          · its names · sub      the rule in one sentence
 *   2  When it alerts      every time / values    the rule exactly as it will be saved
 *                          · AI confidence
 *   3  How serious is it?  severity · repeat
 *   4  Alert message       text · variables
 *   5  More options        fallback text · webhook
 *
 * Main / sub event: event_type · sub_event_type · event_match (CC migration 067). A rule only
 * decides WHETHER something is an alert — cameras, people and channels are "Who gets notified".
 * A rule saved before that may still be limited to some cameras: the form says so.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDownIcon, LinkIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { aaInputCls } from '../alertActions/AlertActionsControls';
import { parseEventTypeTokens, type EventTypeOption } from './EventTypeFields';
import RuleWhen, { whenSentence } from './RuleWhen';
import { fieldsFor, BUILTIN_FIELDS, type EventCatalogEntry } from './ruleConditionsModel';
import {
  GENERIC_TEMPLATE, MESSAGE_VARIABLES, eventChoices, inferChoice, previewMessage, spellings, uniqueNames,
  type EventChoice, type EventFamily,
} from './eventAliases';

export interface AlertRuleFormProps {
  form: Record<string, any>;
  setForm: (next: Record<string, any>) => void;
  cameras: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  catalog: EventCatalogEntry[];
  /** The app's built-in detection list — kept for callers; the form uses eventAliases. */
  builtin?: EventTypeOption[];
  /** Where cameras, people and channels are set — "Who gets notified". */
  routingHref?: string;
}

export const COOLDOWNS = [
  { value: 0, label: 'Every time', long: 'Every time it is seen' },
  { value: 30, label: '30 s', long: '30 seconds' },
  { value: 60, label: '1 min', long: '1 minute' },
  { value: 300, label: '5 min', long: '5 minutes' },
  { value: 900, label: '15 min', long: '15 minutes' },
  { value: 3600, label: '1 h', long: '1 hour' },
];

export const SEVERITIES = [
  { value: '*', label: 'Let the system decide', hint: 'Fire, falls and intrusions are critical; the rest are warnings' },
  { value: 'critical', label: 'Critical', hint: 'Needs action now' },
  { value: 'warning', label: 'Warning', hint: 'Someone should look' },
  { value: 'normal', label: 'For information', hint: 'Nothing to do' },
];
export const SEV_DOT: Record<string, string> = { critical: 'bg-destructive', warning: 'bg-warning', normal: 'bg-info', '*': 'bg-muted-foreground' };
const SEV_ON: Record<string, string> = {
  critical: 'border-red-500 bg-red-500/10',
  warning: 'border-amber-500 bg-amber-500/10',
  normal: 'border-blue-500 bg-blue-500/10',
  '*': 'border-foreground bg-muted',
};
const OTHER = '__other__';
const splitNames = (v: unknown) => String(v ?? '').split(',').map((t) => t.trim()).filter((t) => t && t !== '*');
const labelCls = 'text-[13px] font-semibold text-foreground';

/** A numbered section, divided from the next by a line. */
function Section({ n, title, hint, action, children }: { n: number; title: string; hint?: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex gap-4 py-6 first:pt-5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-[13px] font-semibold text-background">{n}</span>
      <div className="min-w-0 flex-1 space-y-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <h4 className="text-[16px] font-semibold leading-7 text-foreground">{title}</h4>
            {hint && <p className="text-[13px] text-muted-foreground">{hint}</p>}
          </div>
          {action}
        </div>
        {children}
      </div>
    </section>
  );
}

/** The names the rule matches: chips with ✕ and, inside the same box, a field to add one (Enter). */
function NameChips({ names, onChange, empty = 'No name yet.', placeholder = 'Add a name, e.g. fire_catch ↵' }: {
  names: string[]; onChange: (next: string[]) => void; empty?: string; placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    if (!draft.trim()) return;
    onChange(uniqueNames([...names, ...spellings(draft.replace(/,$/, ''))]));
    setDraft('');
  };
  return (
    <div className="rounded-xl border border-border bg-background p-2 focus-within:border-brand">
      <div className="flex flex-wrap gap-1.5">
        {names.length === 0 && <span className="px-1 py-1 text-[12px] text-muted-foreground">{empty}</span>}
        {names.map((n) => (
          <span key={n} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted py-1 pl-2.5 pr-1.5 font-mono text-[12px] text-foreground">
            {n}
            <button type="button" onClick={() => onChange(names.filter((x) => x !== n))} className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground" aria-label={`Remove ${n}`}>
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); }
          else if (e.key === 'Backspace' && !draft && names.length) onChange(names.slice(0, -1));
        }}
        onBlur={add}
        placeholder={placeholder}
        className="mt-1.5 w-full bg-transparent px-1 py-1 font-mono text-[12px] text-foreground outline-none placeholder:text-muted-foreground"
        aria-label="Add a name"
      />
    </div>
  );
}

function Row({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 border-b border-border py-2 last:border-0">
      <dt className="text-[12px] text-muted-foreground">{k}</dt>
      <dd className="min-w-0 break-words text-[13px] text-foreground">{children}</dd>
    </div>
  );
}

export default function AlertRuleForm({ form, setForm, cameras, groups, catalog, routingHref }: AlertRuleFormProps) {
  const set = (patch: Record<string, any>) => setForm({ ...form, ...patch });

  // The main event's names (event_type) and, when considered, the sub event's (sub_event_type).
  const mainNames = parseEventTypeTokens(form.event_type).filter((t) => t !== '*');
  const setMainNames = (next: string[]) => set({ event_type: next.length ? uniqueNames(next).join(',') : '*' });
  const subNames = splitNames(form.sub_event_type);
  const setSubNames = (next: string[]) => set({ sub_event_type: uniqueNames(next).join(',') });
  const [subOn, setSubOn] = useState(subNames.length > 0);
  const match: 'all' | 'any' = form.event_match === 'any' ? 'any' : 'all';
  // Every name the rule matches — which fields the AI sends with it (tests, message variables).
  const names = [...mainNames, ...(subOn ? subNames : [])];

  const families = useMemo(() => eventChoices(catalog || []), [catalog]);
  // What the boxes show: what was picked here, else what the rule's names point at.
  const inferredMain = useMemo(() => inferChoice(mainNames, families), [mainNames.join(','), families]); // eslint-disable-line react-hooks/exhaustive-deps
  const [mainSel, setMainSel] = useState<string | null>(null);
  const main = mainSel ?? (inferredMain.family?.value || OTHER);
  const family: EventFamily | null = families.find((f) => f.value === main) || null;
  const inferredSub = useMemo(() => (family ? inferChoice(subNames, [family]).sub : null), [subNames.join(','), family]); // eslint-disable-line react-hooks/exhaustive-deps
  const [subSel, setSubSel] = useState<string | null>(null);
  const sub = subSel ?? (inferredSub?.value || (subNames.length ? OTHER : ''));
  const subChoice: EventChoice | null = family?.subs.find((x) => x.value === sub) || null;
  // The rule's name follows the picks while it is still one of the event names (not typed).
  const renamable = !String(form.name || '').trim() || families.some((f) => f.label === form.name || f.subs.some((x) => x.label === form.name));

  const pickMain = (v: string) => {
    setMainSel(v);
    setSubSel(null);
    const f = families.find((x) => x.value === v);
    set({
      event_type: f ? uniqueNames(f.aliases).join(',') : '*',
      sub_event_type: '',
      ...(f && renamable ? { name: f.label } : {}),
      ...(f && !String(form.message_template || '').trim() ? { message_template: f.template } : {}),
    });
  };
  const pickSub = (v: string) => {
    setSubSel(v);
    const x = family?.subs.find((y) => y.value === v);
    set({ sub_event_type: x ? uniqueNames(x.aliases).join(',') : '', ...(x && renamable ? { name: x.label } : {}) });
  };
  const toggleSub = (on: boolean) => {
    setSubOn(on);
    setSubSel(null);
    if (!on) {
      set({ sub_event_type: '', ...(renamable && family ? { name: family.label } : {}) });
    } else if (family && inferredMain.sub) {
      // A rule from before main / sub were apart keeps a SUB event's names as its event names:
      // move them to the sub box and give the main box the main event's own names — else "both
      // must match" would compare fire_detected with the main event and never fire.
      setSubSel(inferredMain.sub.value);
      set({ event_type: uniqueNames(family.aliases).join(','), sub_event_type: uniqueNames(inferredMain.sub.aliases).join(',') });
    } else if (family?.subs.length === 1) {
      pickSub(family.subs[0].value);
    }
  };

  // An older rule limited to some cameras (cameras are "Who gets notified"'s now) — shown, and removable.
  const camName = (id: string) => cameras.find((c) => c.id === id)?.name || 'a removed camera';
  const groupName = groups.find((g) => g.id === form.group_id)?.name;
  const listed: string[] = Array.isArray(form.camera_ids) ? form.camera_ids : [];
  const oldLimit = listed.length ? listed.map(camName).join(', ')
    : form.camera_id ? camName(form.camera_id)
      : form.group_id ? `cameras in ${groupName || 'a removed group'}` : '';

  const pct = Math.round((parseFloat(form.min_confidence) || 0) * 100);
  // 0 = confidence not checked (the command-center applies no threshold).
  const usesConfidence = pct > 0;
  const cooldown = Number(form.cooldown_seconds) || 0;
  const severity = form.severity || '*';
  const cooldownKnown = COOLDOWNS.some((c) => c.value === cooldown);

  // Message
  const msgRef = useRef<HTMLTextAreaElement | null>(null);
  const eventFields = useMemo(
    () => Object.keys(fieldsFor(catalog || [], names)).filter((f) => !(f in BUILTIN_FIELDS)).slice(0, 12),
    [catalog, names.join(',')], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [moreVars, setMoreVars] = useState(false);
  const insertVar = (v: string) => {
    const el = msgRef.current;
    const text = String(form.message_template || '');
    const token = `\${${v}}`;
    const at = el ? el.selectionStart ?? text.length : text.length;
    const end = el ? el.selectionEnd ?? at : at;
    set({ message_template: `${text.slice(0, at)}${token}${text.slice(end)}`.slice(0, 500) });
    requestAnimationFrame(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = at + token.length; } });
  };
  const suggested = family?.template || GENERIC_TEMPLATE;
  const template = String(form.message_template || '').trim();
  const [moreOpen, setMoreOpen] = useState(!!form.webhook_enabled);

  // The sentence
  const mainLabel = family?.label || (mainNames.length ? mainNames.slice(0, 2).join(' or ') : 'any event');
  const subLabel = subOn ? subChoice?.label || (subNames.length ? subNames.slice(0, 2).join(' or ') : '') : '';
  const whatText = subLabel ? (match === 'any' ? `${mainLabel} or ${subLabel}` : `${subLabel} (${mainLabel})`) : mainLabel;
  const sevText = severity === '*' ? 'an alert' : severity === 'normal' ? 'an information alert' : `a ${severity} alert`;
  const when = whenSentence(form);
  const alertName = form.name || subChoice?.label || family?.label || 'Alert';
  const preview = template ? previewMessage(template, { alert_name: alertName }) : form.description || 'The event’s own text.';

  const segBtn = (on: boolean) => `px-4 py-2 text-[13px] font-medium transition-colors ${on ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`;

  return (
    // Wide screens: each column scrolls on its own, so the preview stays in view while editing.
    <div className="lg:grid lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_24rem]">
      {/* ── The questions ── */}
      <div className="min-w-0 divide-y divide-border px-6 pb-2 lg:overflow-y-auto">
        {(oldLimit || form.websocket_enabled === false) && (
          <div className="space-y-2 py-4">
            {oldLimit && (
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500 bg-amber-500/10 px-3 py-2.5 text-[13px] text-foreground">
                <span className="min-w-0 flex-1">
                  An older rule: it only raises alerts on <b>{oldLimit}</b>. Cameras are chosen under “Who gets notified” now.
                </span>
                <button type="button" onClick={() => set({ camera_id: null, group_id: null, camera_ids: null })}
                  className="shrink-0 rounded-lg border border-border bg-card px-2.5 py-1 text-[12px] font-medium text-foreground hover:bg-muted">
                  Cover all cameras
                </button>
              </div>
            )}
            {form.websocket_enabled === false && (
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500 bg-amber-500/10 px-3 py-2.5 text-[13px] text-foreground">
                <span className="min-w-0 flex-1">An older setting keeps this rule’s alerts <b>off the alert board</b>.</span>
                <button type="button" onClick={() => set({ websocket_enabled: true })}
                  className="shrink-0 rounded-lg border border-border bg-card px-2.5 py-1 text-[12px] font-medium text-foreground hover:bg-muted">
                  Let “Who gets notified” decide
                </button>
              </div>
            )}
          </div>
        )}

        <Section n={1} title="What to watch for" hint="The rule name is shown on every alert it raises.">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className={labelCls}>Rule name</span>
              <input value={form.name || ''} onChange={(e) => set({ name: e.target.value })} className={`${aaInputCls} mt-1.5`} placeholder="e.g. Fire in the warehouse" />
            </label>
            <label className="block">
              <span className={labelCls}>Main event</span>
              <div className="relative mt-1.5">
                <select value={main} onChange={(e) => pickMain(e.target.value)} className={`${aaInputCls} mt-0 appearance-none pr-9`} aria-label="Main event">
                  {families.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                  <option value={OTHER}>Other — type the event’s name</option>
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>
            </label>
          </div>
          <div>
            <p className="mb-1.5">
              <span className={labelCls}>Matches events named</span>
              <span className="ml-2 text-[12px] text-muted-foreground">Capitals, spaces, “-” and “_” are ignored.</span>
            </p>
            <NameChips names={mainNames} onChange={setMainNames}
              empty={main === OTHER ? 'No name — any event the AI sends. Type one to narrow it.' : 'No name yet.'}
              placeholder={main === OTHER ? 'Type the event name, e.g. belt_movement ↵' : 'Add a name, e.g. fire_catch ↵'} />
          </div>
          <div className="space-y-3">
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" checked={subOn} onChange={(e) => toggleSub(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand" />
              <span>
                <span className={`block ${labelCls}`}>Also match a sub event</span>
                <span className="block text-[12px] text-muted-foreground">One kind of the main event, e.g. “Fire detected” within Fire &amp; smoke.</span>
              </span>
            </label>
            {subOn && (
              <div className="space-y-3 rounded-xl border border-border bg-muted px-4 py-3 sm:ml-7">
                <div className="relative max-w-sm">
                  <select value={sub} onChange={(e) => pickSub(e.target.value)} className={`${aaInputCls} mt-0 appearance-none pr-9`} aria-label="Sub event">
                    <option value="" disabled>Pick a sub event…</option>
                    {(family?.subs || []).map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                    <option value={OTHER}>Other — type the sub event’s name</option>
                  </select>
                  <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                </div>
                <NameChips names={subNames} onChange={setSubNames} empty="No name yet — pick one above or type it." placeholder="Type the sub event name, e.g. fire_detect ↵" />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[12px] text-muted-foreground">Alert when</span>
                  <div className="inline-flex overflow-hidden rounded-lg border border-border">
                    {([['all', 'Both match'], ['any', 'Either one matches']] as const).map(([v, label], i) => (
                      <button key={v} type="button" onClick={() => set({ event_match: v })} aria-pressed={match === v}
                        className={`${segBtn(match === v)} py-1.5 ${i ? 'border-l border-border' : ''}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Section>

        <Section n={2} title="When it alerts">
          <RuleWhen form={form} set={set} catalog={catalog || []} names={names} />
        </Section>

        <Section n={3} title="How serious is it?">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {SEVERITIES.map((s) => {
              const on = severity === s.value;
              return (
                <button key={s.value} type="button" onClick={() => set({ severity: s.value })} aria-pressed={on}
                  className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 text-left transition-colors ${on ? SEV_ON[s.value] : 'border-border bg-card hover:bg-muted'}`}>
                  <span className={`mt-[7px] h-2.5 w-2.5 shrink-0 rounded-full ${SEV_DOT[s.value]}`} />
                  <span>
                    <span className="block text-[14px] font-semibold text-foreground">{s.label}</span>
                    <span className="block text-[12px] text-muted-foreground">{s.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="space-y-2 pt-1">
            <div>
              <h5 className="text-[14px] font-semibold text-foreground">Don’t repeat it for</h5>
              <p className="text-[12px] text-muted-foreground">The same event on the same camera stays quiet until this time has passed.</p>
            </div>
            <div className="inline-flex max-w-full flex-wrap overflow-hidden rounded-xl border border-border">
              {COOLDOWNS.map((c, i) => (
                <button key={c.value} type="button" onClick={() => set({ cooldown_seconds: c.value })} aria-pressed={cooldown === c.value}
                  className={`${segBtn(cooldown === c.value)} ${i ? 'border-l border-border' : ''}`}>
                  {c.label}
                </button>
              ))}
              {!cooldownKnown && <span className={`${segBtn(true)} border-l border-border`}>{cooldown} s</span>}
            </div>
          </div>
        </Section>

        <Section
          n={4}
          title="Alert message"
          hint="Shown on the board, in emails and on WhatsApp."
          action={template !== suggested ? (
            <button type="button" onClick={() => set({ message_template: suggested })}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand bg-brand-light px-3 py-1.5 text-[13px] font-medium text-brand hover:opacity-90">
              <SparklesIcon className="h-4 w-4" /> Use suggested message
            </button>
          ) : undefined}
        >
          <textarea
            ref={msgRef}
            rows={3}
            maxLength={500}
            value={form.message_template || ''}
            onChange={(e) => set({ message_template: e.target.value })}
            placeholder="Leave empty to use the event’s own text"
            className={`${aaInputCls} mt-0 resize-y`}
          />
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground" title="Click a value to put it where the cursor is">Insert</span>
            {MESSAGE_VARIABLES.map((v) => (
              <button key={v.name} type="button" onClick={() => insertVar(v.name)} title={`${v.label}, e.g. ${v.sample}`}
                className="rounded-md border border-border bg-card px-2 py-1 font-mono text-[12px] text-foreground hover:bg-muted">
                {`\${${v.name}}`}
              </button>
            ))}
            {moreVars && eventFields.map((f) => (
              <button key={f} type="button" onClick={() => insertVar(f)} title="Sent by the AI with this event"
                className="rounded-md border border-dashed border-border bg-card px-2 py-1 font-mono text-[12px] text-muted-foreground hover:bg-muted">
                {`\${${f}}`}
              </button>
            ))}
            {eventFields.length > 0 && (
              <button type="button" onClick={() => setMoreVars((v) => !v)} className="px-1.5 py-1 text-[12px] font-medium text-brand hover:underline">
                {moreVars ? 'Fewer' : `+ ${eventFields.length} from this model`}
              </button>
            )}
          </div>
        </Section>

        <section className="py-5">
          <button type="button" onClick={() => setMoreOpen((v) => !v)} aria-expanded={moreOpen}
            className="flex w-full items-center gap-2 text-left text-[14px] font-semibold text-foreground">
            <ChevronDownIcon className={`h-4 w-4 text-muted-foreground transition-transform ${moreOpen ? '' : '-rotate-90'}`} />
            More options
            <span className="text-[12px] font-normal text-muted-foreground">Fallback text · webhook</span>
          </button>
          {moreOpen && (
            <div className="mt-4 space-y-4 pl-6">
              <label className="block">
                <span className={labelCls}>Fallback text</span>
                <span className="ml-2 text-[12px] text-muted-foreground">Used when there is no message above and the AI sends none.</span>
                <input value={form.description || ''} onChange={(e) => set({ description: e.target.value })} className={`${aaInputCls} mt-1.5`} />
              </label>
              <div>
                <label className="flex cursor-pointer items-center gap-2.5">
                  <input type="checkbox" checked={!!form.webhook_enabled} onChange={(e) => set({ webhook_enabled: e.target.checked })} className="h-4 w-4 accent-brand" />
                  <LinkIcon className="h-4 w-4 text-muted-foreground" />
                  <span className={labelCls}>Also send it to another system (webhook)</span>
                </label>
                {form.webhook_enabled && (
                  <input value={form.webhook_url || ''} onChange={(e) => set({ webhook_url: e.target.value })} className={`${aaInputCls} mt-2`} placeholder="https://…" />
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* ── Always in view: how it will read, and what will be saved ── */}
      <aside className="border-t border-border bg-muted lg:overflow-y-auto lg:border-l lg:border-t-0">
        <div className="space-y-5 px-5 pb-5">
          <div className="sticky top-0 z-10 -mx-5 bg-muted px-5 pb-1 pt-5">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">On the alert board</p>
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <p className="flex items-center gap-2 text-[15px] font-semibold text-foreground">
                <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${SEV_DOT[severity] || SEV_DOT['*']}`} />
                <span className="min-w-0 truncate">{alertName}</span>
              </p>
              <p className="mt-1.5 text-[14px] leading-relaxed text-foreground">{preview}</p>
              <p className="mt-2 text-[12px] text-muted-foreground">
                {template ? 'Example values filled in.' : form.description ? 'No message set, so the fallback text is used when the AI sends none.' : 'No message set, so the event’s own text is used.'}
              </p>
            </div>
          </div>

          <p className="text-[13px] leading-relaxed text-foreground">
            Raises <b>{sevText}</b> when <b>{whatText}</b> is seen{oldLimit && <> on <b>{oldLimit}</b></>}
            {when && <> and <b>{when}</b></>}
            {usesConfidence ? <>, if the AI is at least <b>{pct}%</b> sure</> : ''}.
            {cooldown ? <> At most one every <b>{COOLDOWNS.find((c) => c.value === cooldown)?.long || `${cooldown} seconds`}</b> per camera.</> : ''}
            {' '}<span className="text-muted-foreground">Who is told is set under {routingHref ? <a href={routingHref} className="font-medium text-brand hover:underline">Who gets notified</a> : '“Who gets notified”'}.</span>
          </p>

          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Saved as</p>
            <dl>
              <Row k="Name">{form.name || <span className="text-muted-foreground">(the event’s name)</span>}</Row>
              <Row k="Main event names"><span className="font-mono text-[12px]">{mainNames.length ? mainNames.join(', ') : '* (any event)'}</span></Row>
              <Row k="Sub event names">{subOn && subNames.length ? <span className="font-mono text-[12px]">{subNames.join(', ')}</span> : <span className="text-muted-foreground">Not checked</span>}</Row>
              {subOn && subNames.length > 0 && <Row k="Main and sub">{match === 'any' ? 'Either one matches' : 'Both must match'}</Row>}
              {oldLimit && <Row k="Cameras">Only {oldLimit} (older rule)</Row>}
              <Row k="Only when">{when || 'Every time it is seen'}</Row>
              <Row k="AI confidence">{usesConfidence ? `At least ${pct}% sure` : <span className="text-muted-foreground">Not checked</span>}</Row>
              <Row k="Severity">{SEVERITIES.find((s) => s.value === severity)?.label}</Row>
              <Row k="Repeat">{COOLDOWNS.find((c) => c.value === cooldown)?.long || `every ${cooldown} s`}</Row>
              <Row k="Message">{template ? <span className="font-mono text-[12px]">{template}</span> : <span className="text-muted-foreground">The event’s own text</span>}</Row>
              {form.description && <Row k="Fallback text">{form.description}</Row>}
              <Row k="Webhook">{form.webhook_enabled ? <span className="font-mono text-[12px]">{form.webhook_url || '(no address yet)'}</span> : <span className="text-muted-foreground">Off</span>}</Row>
            </dl>
          </div>
        </div>
      </aside>
    </div>
  );
}
